import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadUnpaidFollowupByIdUrl,
  zadUnpaidFollowupCandidatesUrl,
  zadUnpaidFollowupCloseUrl,
  zadUnpaidFollowupContactsUrl,
  zadUnpaidFollowupLinkSaleUrl,
  zadUnpaidFollowupMaarefatiUsersUrl,
  zadUnpaidFollowupMeUrl,
  zadUnpaidFollowupPermissionAuditUrl,
  zadUnpaidFollowupPermissionUrl,
  zadUnpaidFollowupPromisesUrl,
  zadUnpaidFollowupRemindersUrl,
  zadUnpaidFollowupReopenUrl,
  zadUnpaidFollowupsUrl,
  zadUnpaidFollowupSummaryUrl,
  zadUnpaidFollowupTicketUrl,
  zadUnpaidFollowupUnlinkSaleUrl,
  zadUnpaidFollowupUpdateUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {
  extractZadSalesValidationAudits,
  extractZadSalesValidationUsers,
} from '../zad/salesValidationPermissionHelpers'
import {
  buildZadUnpaidQuery,
  extractZadMaarefatiUsers,
  extractZadUnpaidDossier,
  extractZadUnpaidFollowupMe,
  extractZadUnpaidFollowupSummary,
  extractZadUnpaidTicket,
} from '../zad/unpaidFollowupHelpers'

export const fetchMyZadUnpaidFollowupPermission = createAsyncThunk(
  'zadUnpaidFollowups/fetchMe',
  async (_payload: void, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadUnpaidFollowupMeUrl)
      const me = extractZadUnpaidFollowupMe(res.data)
      if (!me) {
        return rejectWithValue({success: false, error: 'Invalid permission response', errorCode: 'VALIDATION_ERROR', status: 500})
      }
      return {success: true as const, me}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadUnpaidFollowupSummary = createAsyncThunk(
  'zadUnpaidFollowups/summary',
  async (query: Record<string, string> | undefined, {rejectWithValue}) => {
    try {
      const qs = query ? buildZadUnpaidQuery(query) : ''
      const res = await axios.get(qs ? `${zadUnpaidFollowupSummaryUrl}?${qs}` : zadUnpaidFollowupSummaryUrl)
      return {success: true as const, summary: extractZadUnpaidFollowupSummary(res.data)}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadUnpaidFollowupCandidates = createAsyncThunk(
  'zadUnpaidFollowups/candidates',
  async (query: Record<string, string | number | undefined>, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadUnpaidFollowupCandidatesUrl}?${buildZadUnpaidQuery(query)}`)
      const data = res.data || {}
      return {
        success: true as const,
        candidates: Array.isArray(data.candidates) ? data.candidates : [],
        total: Number(data.total) || 0,
        totalPages: Number(data.totalPages) || 0,
        scanCap: Number(data.scanCap) || 2000,
        scanned: Number(data.scanned) || 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const lookupZadUnpaidFollowupTicket = createAsyncThunk(
  'zadUnpaidFollowups/ticket',
  async (saleNumber: string, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadUnpaidFollowupTicketUrl}?${buildZadUnpaidQuery({saleNumber})}`)
      return {success: true as const, ticket: extractZadUnpaidTicket(res.data)}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const searchZadUnpaidMaarefatiUsers = createAsyncThunk(
  'zadUnpaidFollowups/maarefati',
  async (payload: {search: string; type?: string}, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadUnpaidFollowupMaarefatiUsersUrl}?${buildZadUnpaidQuery({search: payload.search, type: payload.type})}`
      )
      return {success: true as const, users: extractZadMaarefatiUsers(res.data)}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadUnpaidFollowupDossiers = createAsyncThunk(
  'zadUnpaidFollowups/list',
  async (query: Record<string, string | number | undefined>, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadUnpaidFollowupsUrl}?${buildZadUnpaidQuery(query)}`)
      const data = res.data || {}
      return {
        success: true as const,
        dossiers: Array.isArray(data.dossiers) ? data.dossiers : [],
        total: Number(data.total) || 0,
        totalPages: Number(data.totalPages) || 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadUnpaidFollowupById = createAsyncThunk(
  'zadUnpaidFollowups/detail',
  async (id: string, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadUnpaidFollowupByIdUrl(id))
      const dossier = extractZadUnpaidDossier(res.data)
      if (!dossier) {
        return rejectWithValue({success: false, error: 'Follow-up not found', errorCode: 'FOLLOWUP_NOT_FOUND', status: 404})
      }
      return {success: true as const, dossier}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadUnpaidFollowup = createAsyncThunk(
  'zadUnpaidFollowups/create',
  async (body: Record<string, unknown>, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadUnpaidFollowupsUrl, body)
      const dossier = extractZadUnpaidDossier(res.data)
      return {success: true as const, dossier}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

async function postDossier(url: string, body: Record<string, unknown> | undefined, rejectWithValue: any) {
  try {
    const res = await axios.post(url, body || {})
    return {success: true as const, dossier: extractZadUnpaidDossier(res.data)}
  } catch (error) {
    return rejectWithValue(parseZadApiError(error))
  }
}

export const updateZadUnpaidFollowup = createAsyncThunk(
  'zadUnpaidFollowups/update',
  async (payload: {id: string; body: Record<string, unknown>}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupUpdateUrl(payload.id), payload.body, rejectWithValue)
)

export const linkZadUnpaidFollowupSale = createAsyncThunk(
  'zadUnpaidFollowups/link',
  async (payload: {id: string; body: Record<string, unknown>}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupLinkSaleUrl(payload.id), payload.body, rejectWithValue)
)

export const unlinkZadUnpaidFollowupSale = createAsyncThunk(
  'zadUnpaidFollowups/unlink',
  async (payload: {id: string; body: {saleId: string}}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupUnlinkSaleUrl(payload.id), payload.body, rejectWithValue)
)

export const addZadUnpaidFollowupPromise = createAsyncThunk(
  'zadUnpaidFollowups/promise',
  async (payload: {id: string; body: Record<string, unknown>}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupPromisesUrl(payload.id), payload.body, rejectWithValue)
)

export const addZadUnpaidFollowupContact = createAsyncThunk(
  'zadUnpaidFollowups/contact',
  async (payload: {id: string; body: Record<string, unknown>}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupContactsUrl(payload.id), payload.body, rejectWithValue)
)

export const postZadUnpaidFollowupReminder = createAsyncThunk(
  'zadUnpaidFollowups/reminder',
  async (payload: {id: string; body: Record<string, unknown>}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupRemindersUrl(payload.id), payload.body, rejectWithValue)
)

export const closeZadUnpaidFollowup = createAsyncThunk(
  'zadUnpaidFollowups/close',
  async (payload: {id: string; body?: Record<string, unknown>}, {rejectWithValue}) =>
    postDossier(zadUnpaidFollowupCloseUrl(payload.id), payload.body || {}, rejectWithValue)
)

export const reopenZadUnpaidFollowup = createAsyncThunk(
  'zadUnpaidFollowups/reopen',
  async (id: string, {rejectWithValue}) => postDossier(zadUnpaidFollowupReopenUrl(id), {}, rejectWithValue)
)

export const fetchZadUnpaidFollowupPermissions = createAsyncThunk(
  'zadUnpaidFollowups/permList',
  async (_payload: void, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadUnpaidFollowupPermissionUrl)
      return {success: true as const, users: extractZadSalesValidationUsers(res.data)}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const setZadUnpaidFollowupPermission = createAsyncThunk(
  'zadUnpaidFollowups/permSet',
  async (payload: {userId: string; enabled: boolean}, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadUnpaidFollowupPermissionUrl, {
        userId: payload.userId,
        enabled: payload.enabled === true,
      })
      return {success: true as const, enabled: res.data?.enabled === true}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadUnpaidFollowupPermissionAudit = createAsyncThunk(
  'zadUnpaidFollowups/permAudit',
  async (userId: string | undefined, {rejectWithValue}) => {
    try {
      const url = userId
        ? `${zadUnpaidFollowupPermissionAuditUrl}?userId=${encodeURIComponent(userId)}`
        : zadUnpaidFollowupPermissionAuditUrl
      const res = await axios.get(url)
      return {success: true as const, audits: extractZadSalesValidationAudits(res.data)}
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
